import { formatAppliesTo } from "../types/nda";
import { parseDateOnly } from "./formatDate";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Format as dd-mmm-yy (e.g. 15-Jan-24). */
export function formatNcShortDate(
  dateStr: Date | string | null | undefined,
): string | null {
  if (dateStr == null || dateStr === "") return null;
  const date = parseDateOnly(dateStr);
  if (isNaN(date.getTime())) return null;
  const day = date.getDate().toString().padStart(2, "0");
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${day}-${month}-${year}`;
}

export function nonCircumventBadgeLabel(
  expiryDate: string | null | undefined,
): string {
  const expiry = formatNcShortDate(expiryDate);
  return expiry ? `(NC ${expiry})` : "(NC)";
}

/** True when expiry calendar date is before today (still active on expiry day). */
export function isNdaExpired(
  expiryDate: string | null | undefined,
): boolean {
  if (expiryDate == null || expiryDate === "") return false;
  // Local midnight on the expiry date; new Date('YYYY-MM-DD') is UTC
  // midnight, which is the previous day west of UTC.
  const expiry = parseDateOnly(expiryDate);
  if (isNaN(expiry.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  return expiry.getTime() < today.getTime();
}

export function nonCircumventTooltipTitle(
  expiryDate: string | null | undefined,
  appliesTo?: string | null,
): string {
  const expiry = formatNcShortDate(expiryDate);
  const appliesLabel = formatAppliesTo(appliesTo);
  const appliesSuffix =
    appliesTo && appliesLabel !== "-"
      ? ` Applies to ${appliesLabel}`
      : "";

  if (!expiry) {
    return `Non-Circumvent Clause${appliesSuffix ? `.${appliesSuffix}` : ""}`;
  }
  const verb = isNdaExpired(expiryDate) ? "expired" : "expires";
  return `Non-Circumvent Clause, ${verb} ${expiry}.${appliesSuffix}`;
}

export function formatRestrictionType(
  type: string | null | undefined,
): string {
  if (!type) return "-";
  if (type === "non_circumvent") return "Non-circumvent";
  if (type === "no_contact") return "No contact";
  return type.replace(/_/g, " ");
}
